// The Search singleton tab's centre (pen Q82): the Search page over the
// host's projected pool, on its tab (Materials for a material or package
// facet, else Sources, the default), or the item gone into, under its bar
// (close, prev/next).
//
// Search is a read: the field and the tab are frame state (the URL); the
// library module keeps the sort and the place in the list (pages shown, the
// scroll an item was opened from, so coming back lands there) and runs the
// opened item's read. Opening a material opens its project in the Editor
// at that material; a material in no project, a package or a source opens
// as before. Every row action maps to its one existing command.

import { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect } from "react";
import { focusPagesOf } from "../../../components/Navigation/focusTarget.js";
import { cameFromStudio } from "../../../components/Navigation/studioReturn.js";
import type { FocusTarget } from "../../../components/Navigation/types.js";
import {
    DrillInBar,
    positionIn,
} from "../../../components/Search/DrillInBar.js";
import {
    refOfResult,
    runLibraryRowIntent,
} from "../../../components/Search/libraryRowIntent.js";
import type {
    SearchFacet,
    SearchResult,
} from "../../../components/Search/searchModel.js";
import { SearchPage } from "../../../components/SearchPage/SearchPage.js";
import {
    clipQuery,
    effectiveSort,
    facetOfTab,
    fieldQueryOf,
    materialRowsOf,
    printFieldQuery,
    queryWords,
    shownMaterials,
    tabOfFacet,
    withoutFilters,
} from "../../../components/SearchPage/searchRows.js";
import { projectStudioCards } from "../../../components/Studio/studioCards.js";
import {
    deskReduce,
    emptyDesk,
    openThingCommands,
} from "../../../components/Studio/studioDesk.js";
import { listPlaceOf } from "../../../modules/library/model.js";
import { uploadingFilenames } from "../../ingest.js";
import { useProjectItems } from "../chrome/useProjectItems.js";
import { useAppShell } from "../context.js";
import { ResourceDetail } from "../ResourceDetail.js";
import { openResourceTransition } from "../resourceHome.js";
import { useLibrary } from "../useLibraryModule.js";
import { LibraryMaterial } from "./MaterialCentre.js";

// `kind` is the tab's facet; `query` the field (`?q=`); `selectedId` the
// row gone into (`/library/{id}`), `target` where in it to open.
export const SearchTabCentre = ({
    kind,
    query: rawQuery,
    selectedId,
    target,
}: {
    readonly kind: SearchFacet | undefined;
    readonly query: string;
    readonly selectedId?: string | undefined;
    readonly target?: FocusTarget | undefined;
}): ReactNode => {
    const {
        ports,
        searchResults,
        openRef,
        draftMaterialFromSource,
        submitResourceRename,
        archiveResourceNow,
        unarchiveResourceNow,
        promoteFileNow,
        setConfirmDelete,
        requestUploadForSource,
        openFilePicker,
        libraryMount,
        library,
        ingest,
        dispatch,
        frame,
    } = useAppShell();
    const { t } = useLingui();
    const projects = useProjectItems();
    const model = useLibrary(libraryMount);
    const { send } = libraryMount;
    const tab = tabOfFacet(kind);
    const query = clipQuery(rawQuery);
    const field = fieldQueryOf(query);
    const typing = queryWords(field.text).length > 0;
    const opened = searchResults.find((r) => r.id === selectedId);
    const materialSort = effectiveSort(
        model.sorts.materials,
        "changed",
        typing,
    );
    const sourceSort = effectiveSort(model.sorts.sources, "added", typing);
    // The list the place belongs to: another field, tab or sort starts
    // from the top.
    const listKey = `${tab}\u0000${query}\u0000${JSON.stringify(
        tab === "materials" ? materialSort : sourceSort,
    )}`;
    const place = listPlaceOf(model, listKey);

    // Frame → module: the field and the opened row. Search reads no
    // passages (pen Q82: one title search per tab).
    // biome-ignore lint/correctness/useExhaustiveDependencies: `send` is stable; the opened row is keyed by id + kind.
    useEffect(() => {
        send({
            type: "shown",
            query,
            contentWords: [],
            opened:
                opened === undefined
                    ? null
                    : { id: opened.id, kind: opened.kind },
        });
    }, [query, opened?.id, opened?.kind]);

    // A material in a project: the Editor on that project, at the
    // material (its own page, or the container page holding it).
    const projectOf = (id: string): string | undefined =>
        materialRowsOf(searchResults, library).find(
            (row) => row.result.id === id,
        )?.project?.id;
    const openInEditor = (projectId: string, id: string): void => {
        const project = (library?.projects ?? []).find(
            (p) => p.id === projectId,
        );
        const canvas = new Set((project?.canvas ?? []).map((i) => i.target));
        const cards = projectStudioCards(searchResults, library, canvas).filter(
            (c) => canvas.has(c.id),
        );
        projects.open(
            projectId,
            openThingCommands(cards, id).reduce(deskReduce, emptyDesk),
        );
    };

    const open = (result: SearchResult, scrollTop: number): void => {
        send({ type: "list_left", key: listKey, scrollTop });
        switch (result.kind) {
            case "material": {
                const projectId = projectOf(result.id);
                if (projectId !== undefined && result.archived !== true)
                    openInEditor(projectId, result.id);
                else send({ type: "row_entered", id: result.id });
                return;
            }
            case "package":
                send({ type: "row_entered", id: result.id });
                return;
            case "source":
            case "file": {
                const ref = refOfResult(result);
                if (ref !== null) dispatch(openResourceTransition(ref));
                return;
            }
            case "conversation":
                return;
        }
    };
    const onRowIntent = runLibraryRowIntent({
        renameResource: submitResourceRename,
        archiveResource: archiveResourceNow,
        unarchiveResource: unarchiveResourceNow,
        // The shared confirm dialog; its confirm runs the delete.
        requestDeleteResource: setConfirmDelete,
    });

    if (opened !== undefined && selectedId !== undefined) {
        // Prev/next walk the materials as the Materials tab lists them.
        const order = shownMaterials(
            materialRowsOf(searchResults, library),
            field,
            materialSort,
        ).map((row) => row.result);
        return (
            <section className="bsx-item" aria-label={opened.title}>
                <DrillInBar
                    {...(cameFromStudio(frame.history)
                        ? {
                              back: {
                                  label: t`Back to Studio`,
                                  onBack: () =>
                                      dispatch({ type: "open-studio" }),
                              },
                          }
                        : {})}
                    {...(opened.archived === true ? { note: t`Archived` } : {})}
                    position={positionIn(order, opened.id)}
                    onClose={() => send({ type: "list_returned" })}
                    onStep={(to) => send({ type: "stepped", id: to.id })}
                />
                <div className="bsx-item-body">
                    <ResourceDetail
                        key={opened.id}
                        result={opened}
                        load={
                            model.detail._tag === "Detail.Open" &&
                            model.detail.id === opened.id
                                ? model.detail.load
                                : Remote.loading
                        }
                        onRetry={() => send({ type: "detail_retried" })}
                        focusPages={focusPagesOf(target)}
                        // A material is EDITED in place (the editor host
                        // follows the opened material: editedMaterialRef).
                        renderMaterial={(material) => (
                            <LibraryMaterial
                                key={material.id}
                                title={material.title}
                                target={target}
                            />
                        )}
                        downloadExport={ports.downloadExport}
                        onOpenRef={openRef}
                        onDraftFromSource={draftMaterialFromSource}
                        onReuploadSource={(source, onRestored) =>
                            requestUploadForSource(source.id, source.title, {
                                onRestored,
                            })
                        }
                        onPromoteFile={promoteFileNow}
                    />
                </div>
            </section>
        );
    }

    return (
        <SearchPage
            tab={tab}
            query={query}
            field={field}
            onQuery={(next) =>
                send({ type: "query_edited", query: clipQuery(next) })
            }
            onTab={(next) =>
                dispatch({
                    type: "open-search",
                    kind: facetOfTab(next),
                    query: printFieldQuery(withoutFilters(field)),
                })
            }
            materialSort={materialSort}
            sourceSort={sourceSort}
            onSortMaterials={(sort) => send({ type: "materials_sorted", sort })}
            onSortSources={(sort) => send({ type: "sources_sorted", sort })}
            place={place}
            onShowMore={() => send({ type: "more_shown", key: listKey })}
            summons={model.summons}
            pool={searchResults}
            library={library}
            uploading={tab === "sources" ? uploadingFilenames(ingest) : []}
            onOpen={open}
            onOpenProject={(projectId) => projects.open(projectId)}
            onAddSource={openFilePicker}
            onNewProject={projects.newProject}
            onRowIntent={onRowIntent}
        />
    );
};
